import React, { createContext, useContext, useState, useCallback } from 'react';

export type ValidEnvironment = 'dev' | 'staging' | 'prod';

interface EnvironmentState {
  environment: ValidEnvironment;
  setEnvironment: (env: ValidEnvironment) => void;
}

const ENV_STORAGE_KEY = 'byte_environment';

const EnvironmentContext = createContext<EnvironmentState | null>(null);

function getStoredEnvironment(): ValidEnvironment {
  const stored = localStorage.getItem(ENV_STORAGE_KEY);
  if (stored === 'dev' || stored === 'staging' || stored === 'prod') {
    return stored;
  }
  return 'dev';
}

export function EnvironmentProvider({ children }: { children: React.ReactNode }) {
  const [environment, setEnvironmentState] = useState<ValidEnvironment>(getStoredEnvironment);

  const setEnvironment = useCallback((env: ValidEnvironment) => {
    setEnvironmentState(env);
    localStorage.setItem(ENV_STORAGE_KEY, env);
  }, []);

  return (
    <EnvironmentContext.Provider value={{ environment, setEnvironment }}>
      {children}
    </EnvironmentContext.Provider>
  );
}

export function useEnvironment(): EnvironmentState {
  const context = useContext(EnvironmentContext);
  if (!context) {
    throw new Error('useEnvironment must be used within an EnvironmentProvider');
  }
  return context;
}

/** Color config per environment */
export const ENV_COLORS: Record<
  ValidEnvironment,
  { label: string; bg: string; text: string; dot: string }
> = {
  dev: { label: 'Dev', bg: 'bg-env-dev-bg', text: 'text-env-dev', dot: 'bg-env-dev' },
  staging: {
    label: 'Staging',
    bg: 'bg-env-staging-bg',
    text: 'text-env-staging',
    dot: 'bg-env-staging',
  },
  prod: { label: 'Production', bg: 'bg-env-prod-bg', text: 'text-env-prod', dot: 'bg-env-prod' },
};
